<template>
  <Transition name="toast" appear>
    <div
      v-if="visible"
      class="border-hairline bg-surface font-body relative w-full cursor-pointer overflow-hidden rounded-2xl border shadow-[0_12px_32px_rgba(5,3,40,0.16)]"
      role="status"
      @click="dismiss"
    >
      <div class="flex items-start gap-3 p-4 sm:gap-3.5">
        <span :class="['flex size-9 flex-shrink-0 items-center justify-center rounded-full', accent.icon]">
          <MaterialCheck v-if="type === 'success'" color="currentColor" size="20px" />
          <Warning v-else-if="type === 'warning' || type === 'error'" color="currentColor" size="20px" />
          <InfoCircle v-else color="currentColor" size="20px" />
        </span>

        <div class="min-w-0 flex-1 pt-0.5">
          <p v-if="title" class="font-display text-ink text-[15px] leading-tight font-semibold break-words">
            {{ title }}
          </p>
          <p class="text-muted mt-1 text-sm leading-snug break-words">
            {{ message }}
          </p>
        </div>

        <IconButton size="md" tone="ghost" label="Close notification" class="-m-1.5 shrink-0" @click.stop="dismiss">
          <MaterialCross color="currentColor" size="16px" />
        </IconButton>
      </div>

      <div v-if="showProgress" class="bg-hairline h-0.5 w-full">
        <div :class="['toast-progress-bar h-full', accent.bar]" :style="{ animationDuration: `${timeout}ms` }" />
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'

import InfoCircle from '@/components/svg/InfoCircle.vue'
import MaterialCheck from '@/components/svg/MaterialCheck.vue'
import MaterialCross from '@/components/svg/MaterialCross.vue'
import Warning from '@/components/svg/Warning.vue'
import IconButton from '@/components/ui/IconButton.vue'

// Types
type NotificationType = 'success' | 'warning' | 'error' | 'neutral'

interface Props {
  title?: string
  message: string
  type?: NotificationType
  timeout?: number
  showProgress?: boolean
  autoClose?: boolean
}

// Props with defaults
const props = withDefaults(defineProps<Props>(), {
  type: 'neutral',
  timeout: 5000,
  showProgress: true,
  autoClose: true,
})

// Emits
const emit = defineEmits<{
  dismiss: []
}>()

// State
const visible = ref(true)
let timeoutId: number | null = null

// Per-type accent: a tinted icon badge + a matching progress bar. Tints use
// translucent fills + `dark:` text variants so they read on both the light and
// dark surfaces (dark mode follows prefers-color-scheme, no in-app toggle).
const ACCENTS: Record<NotificationType, { icon: string; bar: string }> = {
  success: { icon: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400', bar: 'bg-emerald-500' },
  warning: { icon: 'bg-amber-500/15 text-amber-600 dark:text-amber-400', bar: 'bg-amber-500' },
  error: { icon: 'bg-red-500/15 text-invoxia-red dark:text-red-400', bar: 'bg-invoxia-red' },
  neutral: { icon: 'bg-primary-500/15 text-primary-600 dark:text-primary-400', bar: 'bg-primary-500' },
}

const accent = computed(() => ACCENTS[props.type])

// Methods
const dismiss = () => {
  visible.value = false
  if (timeoutId) {
    clearTimeout(timeoutId)
  }
  // Small delay to allow transition to complete
  setTimeout(() => {
    emit('dismiss')
  }, 300)
}

// Auto-close functionality
onMounted(() => {
  if (props.autoClose && props.timeout > 0) {
    timeoutId = window.setTimeout(() => {
      dismiss()
    }, props.timeout)
  }
})

onUnmounted(() => {
  if (timeoutId) {
    clearTimeout(timeoutId)
  }
})
</script>

<style scoped>
/* Progress bar shrink — keyframe-driven so it survives re-renders. */
.toast-progress-bar {
  width: 100%;
  animation: progress-shrink linear forwards;
}
@keyframes progress-shrink {
  from {
    width: 100%;
  }
  to {
    width: 0%;
  }
}

/* Enter/leave: slide down from the top on mobile (toasts dock top-center),
   slide in from the right on desktop (top-right stack). */
.toast-enter-active,
.toast-leave-active {
  transition:
    transform 0.3s ease,
    opacity 0.3s ease;
}
.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateY(-12px);
}
@media (min-width: 640px) {
  .toast-enter-from,
  .toast-leave-to {
    transform: translateX(110%);
  }
}
</style>
